import type { CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdvertisingCard } from '@/widgets/advertising-card'
import { DashboardUserMenu } from '@/widgets/dashboard-user-menu'
import { PartnerOffersCarousel } from '@/widgets/partner-offers'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn } from '@/shared/lib'
import { Button, ContentGroup, InputField } from '@/shared/ui'
import { partnerOffers } from '@/shared/content/partner-offers'
import { routes } from '@/shared/config'
import knowledgeBasePreview from '@/shared/assets/dashboard/lk/knowledge-base-preview.png'

type DashboardLkDesktopProps = {
  className?: string
}

const advertisingListStyle: CSSProperties = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.account.advertisingBody),
  color: designTokens.colors.text.strong,
  paddingLeft: designTokens.spacing.accountLkListIndent,
  margin: 0,
  listStyle: 'disc',
  listStylePosition: 'outside',
}

const advertisingEmphasisStyle = {
  fontWeight: 600,
}

const partnerTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.baseSemibold),
  color: designTokens.colors.text.primary,
}

const desktopSectionGap = designTokens.components.account.layout.sectionGap

const searchInputContainerStyle = {
  maxWidth: designTokens.components.account.search.width,
}

const searchInputStyle = {
  height: designTokens.components.account.search.height,
  backgroundColor: designTokens.colors.background.glass,
  borderRadius: designTokens.borders.radius.sm,
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminPattern}`,
  paddingRight: designTokens.components.account.search.paddingRight,
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.base),
  color: designTokens.colors.text.primary,
}

const heroTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.neutral.ink,
  ...typographyStyle(designTokens.typography.onboarding.titleDesktop),
}

const heroBodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.account.heroBody),
}

const heroButtonStyle = {
  minHeight: designTokens.components.account.hero.buttonHeight,
  width: '100%',
  padding: `${designTokens.spacing.md} ${designTokens.spacing.lg}`,
  fontFamily: designTokens.typography.fontFamilies.primary,
}

const heroPreviewStyle = {
  width: designTokens.components.account.hero.previewWidth,
  height: designTokens.components.account.hero.previewHeight,
  borderRadius: designTokens.components.account.hero.previewRadius,
  boxShadow: designTokens.components.account.hero.previewShadow,
  backgroundColor: designTokens.colors.neutral.graphiteDark,
  overflow: 'hidden',
}

export function DashboardLkDesktop({ className }: DashboardLkDesktopProps) {
  const navigate = useNavigate()

  return (
    <div
      className={cn('hidden w-full flex-col lg:flex', className)}
      style={{ gap: desktopSectionGap }}
    >
      <div
        className="flex w-full items-center justify-between"
        style={{ gap: designTokens.spacing.xxl }}
      >
        <InputField
          type="search"
          placeholder="Поиск"
          aria-label="Поиск"
          variant="search"
          containerClassName="w-full"
          containerStyle={searchInputContainerStyle}
          inputClassName="w-full bg-transparent outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
          style={searchInputStyle}
        />
        <DashboardUserMenu />
      </div>
      <div className="flex w-full flex-col" style={{ gap: desktopSectionGap }}>
        <ContentGroup variant="transparentPanel" className="w-full flex-row" gap="24">
          <div
            className="flex h-full flex-col items-start justify-between"
            style={{
              maxWidth: designTokens.components.account.hero.textWidth,
              gap: designTokens.spacing.xxl,
            }}
          >
            <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.xxl }}>
              <p style={heroTitleStyle}>База знаний</p>
              <p style={heroBodyStyle}>
                Экспертные доклады про рост метрик, маркетинг, дизайн, нейросети, управление
                командой — все, что поможет растить бизнес.
              </p>
            </div>
            <Button
              type="button"
              variant="secondarySubmitMobile"
              style={heroButtonStyle}
              onClick={() => navigate(routes.videos)}
            >
              Смотреть кейсы
            </Button>
          </div>
          <div style={heroPreviewStyle}>
            <img
              src={knowledgeBasePreview}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        </ContentGroup>

        <div className="flex w-full" style={{ gap: designTokens.spacing.xxl }}>
          <AdvertisingCard
            variant="desktop"
            title="Бот комьюнити и нетворкинг"
            description={
              <ul style={advertisingListStyle}>
                <li>Мини-приложение с анкетами участников</li>
                <li>Random coffee</li>
                <li>Чат со всеми участниками</li>
                <li>Актуальным новостям</li>
              </ul>
            }
            ctaLabel="Перейти в телеграм"
            onCtaClick={() =>
              window.open('https://t.me/epicgrowth_com_bot', '_blank', 'noopener,noreferrer')
            }
            className="flex-1"
          />
          <AdvertisingCard
            variant="desktop"
            title="Менторская доска"
            description={
              <p style={{ margin: 0 }}>
                Консультируйтесь с экспертами из разных компаний и индустрий для развития бизнеса и
                карьеры. Для участников комьюнити —{' '}
                <strong style={advertisingEmphasisStyle}>
                  бесплатная сессия-знакомство и специальные предложения
                </strong>
                .
              </p>
            }
            ctaLabel="Выбрать ментора"
            className="flex-1"
          />
        </div>

        <ContentGroup variant="transparentPanel" className="w-full" gap="24" justify="start">
          <p style={partnerTitleStyle}>Партнерские скидки для участников комьюнити</p>
          <PartnerOffersCarousel variant="grid" offers={partnerOffers} />
        </ContentGroup>
      </div>
    </div>
  )
}

DashboardLkDesktop.displayName = 'DashboardLkDesktop'
